<template>
    <div>
        <vrm-form
            class="vrm-add-installation vrm-form"
            :class="generalContext() && 'vrm-modal vrm-modal--wider mfp-with-anim'"
            name="addInstallationForm"
        >
            <fieldset class="vrm-form__fieldset">
                <div class="row">
                    <div class="col-sm-12 col-md-10 col-md-offset-1">
                        <h1
                            v-html="
                                $t(`addinstallation.pageTitle`, {
                                    i18nFallback: 'Add installation',
                                })
                            "
                        ></h1>
                        <p
                            v-html="
                                $t(`addinstallation.SELECT_DEVICE`, {
                                    i18nFallback: 'First select the product you want to add:',
                                })
                            "
                        ></p>
                        <div v-for="(deviceData, type, index) in devices" :key="index">
                            <div @click="setDevice(type)">
                                <vrm-add-installation-header
                                    :type="type"
                                    :device-data="deviceData"
                                    :selected-device="selectedDevice"
                                ></vrm-add-installation-header>
                            </div>

                            <vrm-add-installation-drop
                                ref="formFields"
                                :type="type"
                                :devices="devices"
                                :device-data="deviceData"
                                :selected-device="selectedDevice"
                                :context="context"
                            ></vrm-add-installation-drop>
                        </div>
                    </div>
                </div>
            </fieldset>
            <modal-close-handle v-if="generalContext()" />
        </vrm-form>
    </div>
</template>

<style scoped lang="scss"></style>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularDirective } from '@/vue/decorators/angular-interop';

import VrmAddInstallationHeader from '@/vue/components/shared/VrmDeviceGridItem';
import VrmAddInstallationDrop from '@/vue/components/shared/VrmAddInstallationDrop';
import VrmForm from '@/vue/components/forms/VrmForm';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';

@Component({
    components: {
        VrmAddInstallationHeader,
        VrmAddInstallationDrop,
        VrmForm,
        ModalCloseHandle,
    },
    props: {
        context: { type: String, default: 'addInstallation' },
    },
    data: function() {
        return {
            selectedDevice: this.getSelectedDevice(),
            selectedDeviceData: null,
            formFields: null,
        };
    },
    created() {
        let GX_DEVICE = {
            instruction: 'addinstallation.ADD_INSTRUCTION_GX',
            instruction_replace: 'addinstallation.ADD_INSTRUCTION_GX_REPLACE',
            instruction_wifi_mac: 'addinstallation.ADD_INSTRUCTION_GX_WIFI_MAC',
            code_instruction: 'addinstallation.ENTER_CODE_GX',
            code_instruction_replace: 'addinstallation.ENTER_CODE_GX_REPLACE',
            placeholder: 'addinstallation.PLACEHOLDER_GX',
            placeholder_wifi_mac: 'addinstallation.PLACEHOLDER_GX_WIFI_MAC',
        };
        const devices = {
            cerbogx: _.extend({}, GX_DEVICE, { name: 'Cerbo GX / Cerbo-S GX' }),
            ekranogx: _.extend({}, GX_DEVICE, { name: 'Ekrano GX' }),
            vgx: _.extend({}, GX_DEVICE, { name: 'Venus GX' }),
            ccgx: _.extend({}, GX_DEVICE, { name: 'Color Control GX' }),
            egx: _.extend({}, GX_DEVICE, { name: 'EasySolar II GX' }),
            mgx: _.extend({}, GX_DEVICE, { name: 'MultiPlus II GX' }),
            vgr_ver: {
                instruction: 'addinstallation.ADD_INSTRUCTION_VGR_VER',
                code_instruction: 'addinstallation.ENTER_CODE_VGR_VER',
                placeholder: 'addinstallation.PLACEHOLDER_VGR_VER',
                name: 'Victron Global Remote<br />Victron Ethernet Remote',
                altName: 'Victron Global Remote & Victron Ethernet Remote',
            },
            lora: {
                instruction: 'addinstallation.ADD_INSTRUCTION_LORAWAN',
                code_instruction: 'addinstallation.ENTER_CODE_LORAWAN',
                placeholder: 'addinstallation.PLACEHOLDER_GX',
                name: 'VE.Direct LoRaWAN Module',
            },
            globallink: {
                instruction: 'addinstallation.ADD_INSTRUCTION_GLOBALLINK',
                code_instruction: 'addinstallation.ENTER_CODE_GLOBALLINK',
                placeholder: 'addinstallation.PLACEHOLDER_GX',
                name: 'GlobalLink 520',
            },
            ogx: _.extend({}, GX_DEVICE, { name: 'Octo GX' }),
        };

        if (this.context === 'addInstallation') {
            devices['replace_existing'] = _.extend({}, GX_DEVICE, { name: 'Replace GX Device' });
        }

        this.devices = devices;
    },
    methods: {
        setDevice(device) {
            this.$refs.formFields.forEach(element => {
                element.clearModel();
            });

            this.selectedDevice = device;
            this.selectedDeviceData = this.devices[device];

            // Don't scroll if the device is unselected or not on mobile
            if (!device || window.innerWidth >= 750) return;

            setTimeout(() => {
                const deviceBlock = document
                    .querySelector(`#device_form_${device}`)
                    .getBoundingClientRect();
                const headerBlock = document.querySelector('.vrm-header');
                const blockMargin =
                    2 * parseFloat(getComputedStyle(document.documentElement).fontSize);
                const scrollTop =
                    deviceBlock.top + window.scrollY - headerBlock.offsetHeight - blockMargin;

                window.scrollTo({
                    top: scrollTop,
                    behavior: 'smooth',
                });
            }, 100);
        },
        generalContext() {
            return this.context === 'general';
        },
        addInstallationContext() {
            return this.context === 'addInstallation';
        },
        getSelectedDevice() {
            const routeParams = this.$route.params;
            if (routeParams.device) {
                return routeParams.device;
            }
            if (routeParams.productId) {
                return this.getDeviceType(routeParams.productId);
            }
            return null;
        },
        getDeviceType(productId) {
            // Convert the string (e.g., '0xc001') into a number (e.g., 0xC001)
            const parsedProductId = parseInt(productId, 16);
            let deviceType = '';

            switch (parsedProductId) {
                case 0xc001:
                    deviceType = 'ccgx'; // Color Control GX
                    break;
                case 0xc002:
                    deviceType = 'vgx'; // Venus GX
                    break;
                case 0xc006:
                    deviceType = 'ogx'; // Octo GX
                    break;
                case 0xc007:
                case 0xc00b:
                case 0xc00e:
                    deviceType = 'egx'; // EasySolar-II GX
                    break;
                case 0xc008:
                case 0xc00c:
                    deviceType = 'mgx'; // MultiPlus-II GX
                    break;
                case 0xc013:
                    deviceType = 'ekranogx'; // Ekrano GX
                    break;
                case 0xc00a:
                case 0xc012:
                case 0xc014:
                    deviceType = 'cerbogx'; // Cerbo GX
                    break;
                case 0xc021:
                    deviceType = 'globallink'; // GlobalLink 520
                    break;
            }
            return deviceType;
        },
    },
})
@AngularDirective('vrm-add-installation')
export default class VrmAddInstallation extends Vue {}
</script>
